/**
 * B2BTab — Onglet SADAdmin · Client · B2B Cliniques (orchestrateur).
 *
 * Ce fichier a été découpé en 2026-05 pour ramener la complexité sous le
 * seuil maintenable. Il ne contient plus que l'orchestration entre les
 * vues (liste / création / détail) et le chargement des métadonnées.
 *
 * Sous-modules :
 *   - `B2BConstants` : libellés, tons, helpers UI partagés
 *   - `B2BOrdersListView` : `OrdersList` + `MiniDashboard` (avec KPIs Phase A++)
 *   - `B2BCreateOrderForm` : formulaire de création
 *   - `B2BOrderDetail` : détail dossier (workflow complet, Phase A++ inclus)
 *   - `B2BClientFeedback` : carte « retour client » (révision/refus structuré)
 *   - `B2BDeliveryDelayCell` : édition inline du délai par ligne
 *
 * Permissions (basées sur /api/b2b/meta) :
 *   - admin / manager / supervisor → R/W (création + transitions)
 *   - accountant (comptable)        → lecture seule + exports
 *   - autres rôles                  → 403 (n'arrivent pas ici)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Trash2, Eye, Loader2, XCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import ManagerialDashboard from './ManagerialDashboard';
import { ClinicManagementModal, TrashModal } from './B2BAdvancedModals';
import { API, auth } from './B2BConstants';
import { OrdersList, MiniDashboard } from './B2BOrdersListView';
import { CreateOrderForm } from './B2BCreateOrderForm';
import { OrderDetail } from './B2BOrderDetail';

const B2BTab = () => {
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState({ name: 'list' });    // {name: 'list'|'create'|'detail', id?}
  const [refreshKey, setRefreshKey] = useState(0);
  const [quickStatus, setQuickStatus] = useState(null);  // Phase A++ : filtre rapide via KPI tile

  const loadMeta = useCallback(() => {
    setMetaError(null);
    setMeta(null);
    axios.get(`${API}/b2b/meta`, { headers: auth() })
      .then((r) => setMeta(r.data))
      .catch((e) => {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        let msg;
        if (status === 401 || status === 403) {
          msg = `Session expirée ou accès refusé (HTTP ${status}). Reconnectez-vous.`;
        } else if (!e.response) {
          msg = 'Connexion réseau impossible. Vérifiez votre internet.';
        } else {
          msg = `Erreur HTTP ${status} — ${typeof detail === 'string' ? detail : 'serveur indisponible'}`;
        }
        setMetaError(msg);
        toast.error(msg);
      });
    axios.get(`${API}/b2b/clinics`, { headers: auth() })
      .then((r) => setClinics(r.data))
      .catch(() => {});
    axios.get(`${API}/b2b/users`, { headers: auth() })
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const canWrite = useMemo(() => !!meta?.permissions?.can_write, [meta]);
  const role = meta?.permissions?.role;
  const canDelete = useMemo(() => ['admin', 'supervisor'].includes(role), [role]);
  const canTrash = useMemo(() => role === 'admin', [role]);

  const [showClinics, setShowClinics] = useState(false);
  const [showTrash, setShowTrash] = useState(false);

  const reloadClinics = useCallback(() => {
    axios.get(`${API}/b2b/clinics`, { headers: auth() })
      .then((r) => setClinics(r.data))
      .catch(() => {});
  }, []);

  if (!meta) {
    if (metaError) {
      // Diagnostic : un 401 sur /api/b2b/meta indique soit un token expiré/invalide,
      // soit un secret JWT régénéré côté serveur (cas typique après redéploiement).
      // Le bouton "Se reconnecter" purge TOUTE la session locale (token + user cache)
      // pour garantir un état propre, puis force un rechargement sans cache.
      const isAuthError = /HTTP 4(01|03)/.test(metaError);
      const hardLogout = () => {
        try {
          localStorage.removeItem('dermascan_token');
          localStorage.removeItem('dermascan_user');
          // Nettoie aussi les autres clés qui auraient pu être laissées par
          // d'anciennes versions du frontend.
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && /^(dermascan_|sada_|b2b_)/i.test(k)) {
              localStorage.removeItem(k);
            }
          }
          sessionStorage.clear();
        } catch (_e) {
          // localStorage peut être indisponible en navigation privée ; on continue.
        }
        window.location.replace('/?relog=1');
      };
      return (
        <div className="p-8 max-w-md mx-auto text-center" data-testid="b2b-meta-error">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
            <p className="text-red-700 font-semibold mb-2">Impossible de charger SADA B2B</p>
            <p className="text-sm text-zinc-700 mb-3">{metaError}</p>
            {isAuthError && (
              <p className="text-[11px] text-zinc-500 mb-4 italic">
                Si le problème persiste après reconnexion, contactez l&apos;administrateur :
                il peut s&apos;agir d&apos;un secret de session régénéré côté serveur après une
                mise à jour récente.
              </p>
            )}
            <div className="flex gap-2 justify-center">
              <Button onClick={loadMeta} variant="outline" data-testid="b2b-meta-retry">
                <Loader2 className="w-4 h-4 mr-1" /> Réessayer
              </Button>
              <Button
                onClick={hardLogout}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="b2b-meta-relog"
              >
                Vider la session &amp; se reconnecter
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="p-6 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="b2b-tab-root">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-7 h-7 text-amber-600" />
          <div>
            <h2 className="text-xl font-bold text-zinc-900">SADA B2B · Demandes cliniques</h2>
            <p className="text-sm text-zinc-500">Suivi des dossiers cliniques (cotation, exécution, paiement).</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!canWrite && (
            <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50">
              <Eye className="w-3 h-3 mr-1" /> Lecture seule (comptable)
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowClinics(true)} data-testid="b2b-open-clinics">
            <Building2 className="w-4 h-4 mr-1" /> Fiches cliniques
          </Button>
          {canTrash && (
            <Button variant="outline" size="sm" onClick={() => setShowTrash(true)} className="border-zinc-300" data-testid="b2b-open-trash">
              <Trash2 className="w-4 h-4 mr-1" /> Corbeille
            </Button>
          )}
        </div>
      </div>

      {view.name === 'list' && (
        <>
          <ManagerialDashboard clinics={clinics} users={users} refreshKey={refreshKey} />
          <MiniDashboard refreshKey={refreshKey} onFilterStatus={(s) => setQuickStatus(s)} />
          <OrdersList
            meta={meta}
            canWrite={canWrite}
            refreshKey={refreshKey}
            quickStatus={quickStatus}
            onOpen={(id) => setView({ name: 'detail', id })}
            onCreate={() => setView({ name: 'create' })}
            onCatalogChanged={() => setRefreshKey((k) => k + 1)}
          />
        </>
      )}

      {view.name === 'create' && (
        <Card className="border-zinc-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-zinc-900">Nouveau dossier B2B</h3>
              <Button variant="ghost" size="sm" onClick={() => setView({ name: 'list' })}>
                <XCircle className="w-4 h-4 mr-1" /> Fermer
              </Button>
            </div>
            <CreateOrderForm
              meta={meta}
              clinics={clinics}
              onCancel={() => setView({ name: 'list' })}
              onCreated={(o) => { setRefreshKey((k) => k + 1); setView({ name: 'detail', id: o.id }); }}
            />
          </CardContent>
        </Card>
      )}

      {view.name === 'detail' && (
        <OrderDetail
          orderId={view.id}
          meta={meta}
          canWrite={canWrite}
          canDelete={canDelete}
          clinics={clinics}
          onBack={() => setView({ name: 'list' })}
          onChanged={() => setRefreshKey((k) => k + 1)}
          onDeleted={() => { setRefreshKey((k) => k + 1); setView({ name: 'list' }); }}
        />
      )}

      <ClinicManagementModal
        open={showClinics}
        onClose={() => setShowClinics(false)}
        canWrite={canWrite}
        canDelete={canDelete}
        onChanged={reloadClinics}
      />
      {canTrash && (
        <TrashModal
          open={showTrash}
          onClose={() => setShowTrash(false)}
          onRestored={() => { setRefreshKey((k) => k + 1); reloadClinics(); }}
        />
      )}
    </div>
  );
};

export default B2BTab;
